import React, { useEffect, useState, useRef } from 'react';
import {
  TrashIcon,
  PlusIcon,
  BanknotesIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import ModalDelete from '../../components/modal-delete/modal-delete';
import Notification from '../../components/notification/notification';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import financeApi from '../../services/financeApi';
import {
  PageHeader,
  TWButton,
  TWInput,
  TWModal,
  TWDataTable,
  IconBadge,
  EmptyState,
  FieldLabel,
} from '../../components/ui';
import { headerCls, EditAction } from '../../data/columns';

const cn = (...c) => c.filter(Boolean).join(' ');

const FinanceTypes = () => {
  const [financeList, setFinanceTypeList] = useState([]);
  const [deletedFinanceType, setDeletedFinanceType] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    code: 0,
    multiplier: 1,
  });
  const [dialogs, setDialogs] = useState({ upsert: false, delete: false });
  const [errors, setErrors] = useState({ name: false, code: false });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const cancelButtonRef = useRef(null);

  const getFinanceTypeList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setFinanceTypeList([]);
    try {
      const query = `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;
      const list = await financeApi.getTypeList(query);
      if (list.data.data.length > 0) {
        setFinanceTypeList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onFormChange = (val, key) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const onClickAdd = () => {
    setForm({ id: '', name: '', multiplier: 1, code: 0 });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (val) => {
    setForm({
      id: val.id,
      name: val.name,
      code: val.code,
      multiplier: val.multiplier,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (!form.multiplier || !form.name) {
      setErrors({
        name: !form.name,
        multiplier: !form.multiplier,
      });
      return;
    }
    setErrors({ name: false, multiplier: false });
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      await financeApi.upsertType({ ...form });
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      await getFinanceTypeList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors({ name: false, multiplier: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (financeType) => {
    setDeletedFinanceType(financeType);
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await financeApi.deleteTypeById(deletedFinanceType.id);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getFinanceTypeList();
    }
  };

  useEffect(() => {
    getFinanceTypeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  const columns = [
    {
      title: <span className={headerCls}>Name</span>,
      key: 'name',
      render: (_, finance) => {
        const isIncome = finance.multiplier === 1;
        return (
          <div className="flex items-center gap-3">
            <IconBadge
              icon={TagIcon}
              tone={isIncome ? 'emerald' : 'rose'}
              size="md"
            />
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {finance.name}
            </span>
          </div>
        );
      },
    },
    {
      title: <span className={headerCls}>Code</span>,
      key: 'code',
      render: (_, finance) => (
        <span className="text-sm tabular-nums text-slate-600 dark:text-slate-300">
          {finance.code || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Multiplier</span>,
      key: 'multiplier',
      render: (_, finance) => {
        const isIncome = finance.multiplier === 1;
        return (
          <span
            className={cn(
              isIncome
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
              'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold',
            )}
          >
            {isIncome ? (
              <>
                <PlusCircleIcon className="h-3.5 w-3.5" />
                Income
              </>
            ) : (
              <>
                <MinusCircleIcon className="h-3.5 w-3.5" />
                Expense
              </>
            )}
          </span>
        );
      },
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, finance) => (
        <EditAction
          onClick={() => onClickEdit(finance)}
          label="Edit finance type"
        />
      ),
    },
  ];

  const FinanceTypesSkeleton = () => (
    <div className="reveal" aria-hidden="true">
      <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
        <thead>
          <tr>
            <th className="py-3.5 pr-3 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Name
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Code
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Multiplier
            </th>
            <th className="py-3.5 px-4 text-right">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
          {[...Array(5)].map((_, idx) => (
            <tr key={idx}>
              <td className="whitespace-nowrap py-4 pr-3">
                <div className="flex items-center gap-3">
                  <SkeletonBlock className="h-9 w-9 !rounded-md" />
                  <SkeletonBlock className="h-4 w-32" />
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-4 w-12" />
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-6 w-20 !rounded-full" />
              </td>
              <td className="whitespace-nowrap px-4 py-4 text-right">
                <SkeletonBlock className="ml-auto h-8 w-8 !rounded-full" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Finance Types"
          description="Define finance categories used across transactions. Use the multiplier to mark inflows (+) or outflows (−)."
          actions={
            <TWButton
              variant="primary"
              icon={PlusIcon}
              label="Add Finance Type"
              onClick={onClickAdd}
            />
          }
        />

        {loading.listLoading ? (
          <FinanceTypesSkeleton />
        ) : financeList.length > 0 ? (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={financeList}
              rowKey="id"
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} finance types`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        ) : (
          <EmptyState
            icon={BanknotesIcon}
            title="No finance types yet"
            description="Add your first finance type to start categorizing transactions."
            action={
              <TWButton
                variant="primary"
                icon={PlusIcon}
                label="Add Finance Type"
                onClick={onClickAdd}
              />
            }
          />
        )}
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={form.id ? 'Edit Finance Type' : 'Add Finance Type'}
        description={
          form.id
            ? 'Update the details for this finance type.'
            : 'Create a new category for financial transactions.'
        }
        size="xl"
        initialFocus={cancelButtonRef}
        showCloseButton
        footer={
          <div className="flex w-full items-center justify-between">
            <div>
              {form.id && (
                <TWButton
                  variant="danger"
                  icon={TrashIcon}
                  label="Delete"
                  loading={loading.deleteLoading}
                  disabled={loading.deleteLoading}
                  onClick={() => onClickDelete(form)}
                />
              )}
            </div>
            <div className="flex gap-2">
              <TWButton
                ref={cancelButtonRef}
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseUpsert}
              />
              <TWButton
                variant="primary"
                label={
                  loading.upsertLoading
                    ? 'Saving…'
                    : form.id
                      ? 'Save Changes'
                      : 'Add Finance Type'
                }
                loading={loading.upsertLoading}
                disabled={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <TWInput
            label="Finance Type Name"
            required
            value={form.name}
            onChange={(e) => onFormChange(e.target.value, 'name')}
            placeholder="e.g. Subscriptions, One Time"
            error={
              errors.name ? 'Finance Type Name can not be empty' : undefined
            }
          />

          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-5 sm:col-span-3">
              <TWInput
                label="Code"
                value={form.code}
                onChange={(e) => onFormChange(e.target.value, 'code')}
                placeholder="50"
              />
            </div>
            <div className="col-span-5 sm:col-span-2">
              <FieldLabel required>Multiplier</FieldLabel>
              <div className="inline-flex h-[42px] w-full items-center rounded-md border border-slate-200 bg-slate-50 p-1 shadow-inner dark:border-slate-700 dark:bg-slate-900/60">
                <button
                  type="button"
                  onClick={() => onFormChange(1, 'multiplier')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-md px-3 py-1.5 text-xs font-bold transition',
                    form.multiplier === 1
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                  )}
                >
                  <PlusCircleIcon className="h-4 w-4" />
                  Income
                </button>
                <button
                  type="button"
                  onClick={() => onFormChange(-1, 'multiplier')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-md px-3 py-1.5 text-xs font-bold transition',
                    form.multiplier === -1
                      ? 'bg-rose-500 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                  )}
                >
                  <MinusCircleIcon className="h-4 w-4" />
                  Expense
                </button>
              </div>
            </div>
          </div>
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Finance Type"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedFinanceType}
      />
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default FinanceTypes;
